/**
 * 클라이언트 모듈 스코프 TTL 캐시 + in-flight 디듀프.
 *
 * 기출 회차 완주(JCG-2242 #1)와 감자시험 도전 횟수(JCG-3865)가 공유한다. 모듈 스코프에 두면 탭 1개당
 * 1개이며 클라이언트 네비게이션 동안 유지된다(F5 시 초기화). 그리드·카드를 넘길 때마다 같은 결과를
 * 재조회하던 낭비를 TTL 당 1회로 묶는다.
 */
export interface ClientTtlCache<T> {
  /** 신선한 캐시가 있으면 그대로, 없으면 한 번만 불러온다. 실패하면 직전 스냅샷(없으면 null)을 돌려준다. */
  load(): Promise<T | null>;
  /** 캐시를 비운다. 진행 중이던 요청은 결과를 버리고 최신값으로 다시 불러온다. */
  clear(): void;
  /** 캐시된 값을 바꾼다. 캐시가 비어 있으면 아무것도 하지 않고 false 를 돌려준다. */
  update(updater: (current: T) => T): boolean;
  /** useSyncExternalStore 용 스냅샷·구독 */
  getSnapshot(): T | null;
  subscribe(listener: () => void): () => void;
}

/**
 * @param fetcher 서버에서 값을 불러온다. 실패는 null 로 돌려주며 캐시하지 않는다(다음 load 에서 재시도).
 *   실패를 빈 값으로 캐시하면 로딩 가드가 풀려 "미완주"처럼 잘못 표시되므로 반드시 null 로 구분한다.
 */
export function createClientTtlCache<T>(
  fetcher: () => Promise<T | null>,
  ttlMs: number,
): ClientTtlCache<T> {
  let cached: T | null = null;
  let cachedAt = 0;
  let inflight: Promise<T | null> | null = null;
  // clear 가 증가시켜, 캐시를 비우는 도중 진행 중이던 호출이 stale 결과를 되살리는 레이스를 막는다.
  let generation = 0;
  const listeners = new Set<() => void>();

  const emit = () => {
    for (const listener of listeners) listener();
  };

  const load = (): Promise<T | null> => {
    if (cached !== null && Date.now() - cachedAt < ttlMs) {
      return Promise.resolve(cached);
    }
    if (inflight) return inflight;

    const gen = generation;
    inflight = (async () => {
      try {
        const result = await fetcher();
        // 호출 도중 clear 가 끼어들었으면 이 응답은 stale 일 수 있으므로 반영하지 않고 다시 불러온다.
        if (gen !== generation) return load();
        if (result !== null) {
          cached = result;
          cachedAt = Date.now();
          emit();
        }
        return cached;
      } finally {
        // 자신이 만든 inflight 만 정리한다(clear 로 새 호출이 들어선 경우 건드리지 않음).
        if (gen === generation) inflight = null;
      }
    })();
    return inflight;
  };

  return {
    load,
    clear() {
      cached = null;
      cachedAt = 0;
      inflight = null;
      generation++;
      emit();
    },
    update(updater) {
      if (cached === null) return false;
      cached = updater(cached);
      emit();
      return true;
    },
    getSnapshot: () => cached,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
